import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { brokerFab, brokers } from '@/mocks/brokers';

export default function BrokerFab() {
  const [open, setOpen] = useState(false);
  const [nearFooter, setNearFooter] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const brokerCount = brokers.length;
  const countLabel = `${brokerCount} ${brokerFab.countUnit}`;
  const triggerLabel = `${open ? brokerFab.ariaClose : brokerFab.ariaOpen} (${countLabel})`;
  const hidden = nearFooter;

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    setOpen(false);
  }, [nearFooter]);

  useEffect(() => {
    const footer = document.querySelector('footer');
    if (!footer) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      const onScroll = () => {
        const rect = footer.getBoundingClientRect();
        setNearFooter(rect.top < window.innerHeight - 96);
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      return () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => setNearFooter(entry.isIntersecting));
      },
      { root: null, rootMargin: '0px 0px -96px 0px', threshold: 0 },
    );
    observer.observe(footer);
    return () => observer.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  return (
    <div
      ref={wrapRef}
      aria-hidden={hidden}
      className={`fab-wrap fixed bottom-4 right-4 z-40 flex flex-col items-end gap-3 transition-all duration-300 ease-out md:bottom-6 md:right-6 ${
        hidden ? 'pointer-events-none translate-y-4 opacity-0' : 'translate-y-0 opacity-100'
      }`}
    >
      {open && (
        <div
          id="broker-fab-panel"
          className="fab-panel w-[19rem] max-w-[calc(100vw-2rem)] rounded-xl border border-background-200 bg-background-50 p-3.5"
        >
          <div className="flex items-start justify-between gap-2 px-1">
            <div className="min-w-0">
              <h3 className="font-heading text-[13px] font-semibold text-foreground-950">
                {brokerFab.panelTitle}
              </h3>
              <p className="mt-0.5 text-[10.5px] leading-relaxed text-foreground-500">
                {brokerFab.panelSubtitle}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={brokerFab.close}
              className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-800"
            >
              <i className="ri-close-line text-base" />
            </button>
          </div>

          <ul className="mt-3 flex flex-col gap-1 border-t border-background-200 pt-3">
            {brokers.map((broker) => (
              <li key={broker.id}>
                <Link
                  to={`/forex-trading#${broker.anchor}`}
                  onClick={() => setOpen(false)}
                  className="group flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 transition-colors hover:bg-background-100"
                >
                  <img
                    src={broker.logo}
                    alt={`${broker.name} logo`}
                    title={broker.name}
                    className="h-8 w-8 shrink-0 rounded-md border border-background-200 bg-background-100 object-contain"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-heading text-[12.5px] font-semibold leading-tight text-foreground-950">
                      {broker.name}
                    </span>
                    <span className="mt-0.5 block truncate text-[10.5px] text-foreground-500">
                      {broker.tagline}
                    </span>
                  </span>
                  <i className="ri-arrow-right-line text-sm text-foreground-400 transition-transform group-hover:translate-x-0.5 group-hover:text-primary-600" />
                </Link>
              </li>
            ))}
          </ul>

          <Link
            to="/forex-trading#brokers"
            onClick={() => setOpen(false)}
            className="mt-2.5 flex cursor-pointer items-center justify-between gap-2 rounded-lg border border-background-200 bg-background-100/70 px-3 py-2.5 text-[11.5px] font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            {brokerFab.allLink}
            <i className="ri-equalizer-2-line text-sm" />
          </Link>

          <p className="mt-2 flex items-center gap-1.5 px-1 text-[10px] text-foreground-500">
            <i className="ri-information-line text-[11px] text-accent-600" />
            {brokerFab.pendingNote}
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-controls="broker-fab-panel"
        aria-label={triggerLabel}
        tabIndex={hidden ? -1 : undefined}
        title={`${brokerFab.label} · ${countLabel} ${brokerFab.countHint}`}
        className="fab-trigger group relative flex h-12 w-12 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-0 text-[13px] font-medium text-background-50 transition-all duration-300 hover:bg-primary-600 sm:h-auto sm:w-auto sm:px-4 sm:py-3"
      >
        <i
          className={`text-lg transition-transform duration-300 ${
            open ? 'ri-close-line' : 'ri-user-add-line'
          } sm:text-base`}
        />
        <span className="hidden sm:inline">{brokerFab.label}</span>
        <span className="inline-flex items-center justify-center rounded-full bg-accent-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-background-50 transition-transform duration-300 group-hover:scale-105 max-sm:absolute max-sm:-right-1 max-sm:-top-1 max-sm:h-[18px] max-sm:min-w-[18px] max-sm:border-2 max-sm:border-background-50 max-sm:px-1 max-sm:text-[9.5px]">
          {countLabel}
        </span>
      </button>
    </div>
  );
}